import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Curious } from '@curi/react';
import __map from 'lodash/map';
import __snakeCase from 'lodash/snakeCase';

import './HowItWorksWrapper.css';

import PageHeader from '../../components/global/PageHeader';
import Button from '../../components/elements/Button';
import Slider from 'react-slick';
import { sendAnalyticsEvent } from '../../services/analytics';
import { gtmPageviewEvent } from '../../services/google-tag-manager';
const imgURL = process.env.REACT_APP_CLOUDFRONT_URL;

class HowItWorks extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            puzzle_details_height: 450, //hardcode for smooth transitions
            selected_puzzle: 0,
            selected_puzzle_image: '',
            selected_puzzle_level: '',
            selected_puzzle_type: '',
            selected_puzzle_type_desc: '',
            selected_puzzle_ideologies: [],
            puzzle_image_loaded: false,
            puzzle_animating: false
        }
    }

    componentDidMount() {
        const { response: { location } } = this.props;
        gtmPageviewEvent({
          pageName: 'codespark.com: How It Works',
          componentProps: this.props,
        });

        sendAnalyticsEvent('web_how_it_works', {
            'app': 'web-site',
            'page_URL': location.pathname,
        }, true);

        document.addEventListener('scroll', this.illustrationInViewport);
        this.getPuzzleDetails(this.state.selected_puzzle);
    }

    componentWillUnmount() {
        document.removeEventListener('scroll', this.illustrationInViewport);
    }

    illustrationInViewport() {
        let rect = document.getElementById('coding-illustration').getBoundingClientRect();
        let inOrPassViewport = window.scrollY >= rect.top + rect.height + 300; //buffer
        let explanations = document.querySelectorAll('.app-explanation .statement');

        if (inOrPassViewport) {
            for (let i = 0; i < explanations.length; i++) {
                explanations[i].classList.add('in-view');
            }
        } else {
            for (let i = 0; i < explanations.length; i++) {
                explanations[i].classList.remove('in-view');
            }
        }
    }

    collapseSection(element) {
        let sectionHeight = this.state.puzzle_details_height;
        let elementTransition = element.style.transition;
        element.style.transition = '';

        requestAnimationFrame(function() {
            element.style.height = sectionHeight + 'px';
            element.style.transition = elementTransition;

            requestAnimationFrame(function() {
                element.style.height = 0 + 'px';
            });
        });

        element.setAttribute('data-collapsed', 'true');
    }

    expandSection(element, callback) {
        let sectionHeight = this.state.puzzle_details_height;
        element.style.height = sectionHeight + 'px';
        element.addEventListener('transitionend', function da(e) {
            element.removeEventListener('transitionend', da);
            element.style.height = null;
            callback();
            element.setAttribute('data-collapsed', 'false');
        });

    }

    getPuzzleDetails(index, callback = null) {
        let puzzleImage;
        let puzzleLevel;
        let puzzleType;
        let puzzleTypeDesc;
        let puzzleIdeologies;

        switch(index) {
            case 0:
                puzzleImage = `${imgURL}/flagship/how-it-works/lp_donut_detective_lg.jpg`;
                puzzleLevel = 'Beginner';
                puzzleType = 'Sequencing';
                puzzleTypeDesc = 'The step-by-step order in which instructions should occur';
                puzzleIdeologies = ['reading', 'math', 'pattern recognition'];
                break;
            case 1:
                puzzleImage = `${imgURL}/flagship/how-it-works/lp_tool_trouble_lg.jpg`;
                puzzleLevel = 'Beginner';
                puzzleType = 'Loops';
                puzzleTypeDesc = 'A set of instructions that is repeated over and over again';
                puzzleIdeologies = ['problem solving', 'pattern recognition'];
                break;
            case 2:
                puzzleImage = `${imgURL}/flagship/how-it-works/lp_kite_plight_lg.jpg`;
                puzzleLevel = 'Intermediate';
                puzzleType = 'Advanced Sequencing';
                puzzleTypeDesc = 'Reinforces the core computer science concept of sequencing';
                puzzleIdeologies = ['reading', 'problem solving', 'pattern recognition'];
                break;
            case 3:
                puzzleImage = `${imgURL}/flagship/how-it-works/lp_puppy_problems_lg.jpg`;
                puzzleLevel = 'Advanced';
                puzzleType = 'Events';
                puzzleTypeDesc = 'An action or cue that signals a new line of code to run';
                puzzleIdeologies = ['logic', 'problem solving'];
                break;
            case 4:
                puzzleImage = `${imgURL}/flagship/how-it-works/lp_lunch_crunch_lg.jpg`;
                puzzleLevel = 'Advanced';
                puzzleType = 'Conditional Statements';
                puzzleTypeDesc = 'Actions that execute based on certain provisions';
                puzzleIdeologies = ['math', 'logic', 'problem solving'];
                break;

            default:
                break;
        }

        this.setState({
            selected_puzzle_image: puzzleImage,
            selected_puzzle_level: puzzleLevel,
            selected_puzzle_type: puzzleType,
            selected_puzzle_type_desc: puzzleTypeDesc,
            selected_puzzle_ideologies: puzzleIdeologies,
        }, () => {
            if (callback) {
                callback();
            }
        });
    }

    changePuzzleDetails(index) {
        if (this.state.puzzle_animating || this.state.selected_puzzle === index) {
            return false;
        }

        this.setState({
            puzzle_animating: true,
            puzzle_image_loaded: false
        });

        let puzzleDetails = document.getElementById('puzzle-details');
        this.collapseSection(puzzleDetails);

        this.setState({ selected_puzzle: index }, () => {
            setTimeout(() => {
                this.getPuzzleDetails(index, () => {
                    let expandInterval = setInterval(() => {
                        if (this.state.puzzle_image_loaded) {
                            clearInterval(expandInterval);
                            this.expandSection(puzzleDetails, () => {
                                this.setState({
                                    puzzle_image_loaded: false,
                                    puzzle_animating: false
                                });
                            });
                        }
                    }, 50);
                });
            }, 400);
        });
    }

    render() {
        const slickSettings = {
            arrows: true,
            draggable: false,
            slidesToShow: 3,
            centerMode: true,
            centerPadding: 0,
            className: 'foo-characters-slider'
        };

        return (
            <div className="how-it-works-page page">
                <Helmet>
                    <title>How it Works | Coding App for Kids | codeSpark</title>
                    <meta name="description" content="codeSpark teaches kids how to code with mini games and makes learning computer programming fun and engaging. The loveable Foos teach the basics of programming through creative play and video game creation." />
                    <meta name="keywords" content="Kids coding languages, Hour of code, coding for kids, kids apps, kids games, learn to code, STEM, Reading, Math, Elementary school" />
                    <meta property="og:title" content="Coding Game for Kids - Try it Free!" />
                    <meta property="og:description" content="Try the #1 learn-to-code app for kids ages 3-10. Used by over 20 million kids worldwide, kids learn coding basics with puzzles and build skills to make their own games." />
                    <meta name="twitter:card" content="app" />
                    <meta name="twitter:site" content="@codesparkapp" />
                    <meta name="twitter:description" content="Try the #1 learn-to-code app for kids 4+. Used by over 20 Million kids, codeSpark teaches coding basics through creative play and game creation. Coding improves STEM, reading, and math skills." />
                    <meta name="twitter:app:country" content="US" />
                    <meta name="twitter:app:id:iphone" content="923441570" />
                    <meta name="twitter:app:id:ipad" content="923441570" />
                    <meta name="twitter:app:id:googleplay" content="org.codespark.thefoos" />
                </Helmet>
                <PageHeader text="CODESPARK" />
                <div className="grid-x grid-padding-x">
                    <div className="small-12 cell page-subtitle-section">
                        <p>A great way for your kid <br className="hide-for-medium" />to learn how to code.</p>
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-intro-section">
                    <div className="marketing-shape left">For Kids<br aria-hidden="true"/>3-10</div>
                    <div className="marketing-shape right">STEM<br aria-hidden="true" />Benefits</div>
                    <div className="small-12 medium-6 cell how-intro-section--image">
                        <img alt="" src={`${imgURL}/flagship/how-it-works/intro_gameplay.jpg`} aria-hidden="true" />
                    </div>
                    <div className="small-12 medium-6 cell how-intro-section--points">
                        <ul>
                            <li>Completely word-free.</li>
                            <li>Based on research-backed curriculum from MIT and Princeton.</li>
                            <li>Built with girls in mind without pandering.</li>
                            <li>Self-directed - no experience required.</li>
                        </ul>
                    </div>
                    <div className="show-for-medium how-intro-section--image-2">
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-benefits-section align-center">
                    <div className="small-12 cell arch-border-top"></div>
                    <div className="small-12 cell how-benefits-section--title">
                        <h1 className="section-title">The Benefits of Learning Code</h1>
                    </div>
                    <div className="small-12 cell how-benefits-section--points">
                        <div className="point-block">
                            <p className="point-block--title">Get a head start in STEM</p>
                            <p className="point-block--detail">
                                Ignite interest in STEM by showing how science and technology can
                                be creative and fun.
                            </p>
                        </div>
                        <div className="point-block">
                            <p className="point-block--title">Pathway to Math and Reading</p>
                            <p className="point-block--detail">
                                Sequencing skills learned in codeSpark are the building
                                blocks to early literacy and mathematics.
                            </p>
                        </div>
                        <div className="point-block">
                            <p className="point-block--title">Breakdown Stereotypes</p>
                            <p className="point-block--detail">
                                The earlier kids are exposed to programming, the less likely
                                they are to demonstrate gendered stereotypes about STEM careers.
                            </p>
                        </div>
                    </div>
                    <div className="small-12 cell how-benefits-section--ideologies">
                        <ul>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_math.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Math</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_reading.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Reading</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_logic.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Logic</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_problem_solving.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Problem<br aria-hidden="true" />Solving</p>
                            </li>
                        </ul>
                    </div>
                    <div className="small-12 cell how-benefits-section--ideologies">
                        <ul>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_creativity.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Creativity</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_empathy.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Empathy</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_pattern_recognition.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Pattern<br aria-hidden="true" />Recognition</p>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="grid-x grid-padding-x show-for-medium how-coding-section" aria-hidden="true">
                    <div className="small-12 cell">
                        <h1 className="section-title">What coding looks like in codeSpark</h1>
                    </div>
                    <div id="coding-illustration" className="small-12 cell how-coding-section--illustration">
                        <div className="image-container">
                            <img alt="" src={`${imgURL}/flagship/how-it-works/level_2_6.jpg`} />
                            <div className="app-explanation app-explanation--1">
                                <div className="grabber">+</div>
                                <div className="statement">The Glitch (causes trouble)</div>
                            </div>
                            <div className="app-explanation app-explanation--2">
                                <div className="grabber">+</div>
                                <div className="statement">Woz (executes instructions)</div>
                            </div>
                            <div className="app-explanation app-explanation--3">
                                <div className="grabber">+</div>
                                <div className="statement">The coding tray (drag code here)</div>
                            </div>
                            <div className="app-explanation app-explanation--4">
                                <div className="grabber">+</div>
                                <div className="statement">Commands (tells Woz what to do)</div>
                            </div>
                            <div className="app-explanation app-explanation--5">
                                <div className="grabber">+</div>
                                <div className="statement">Loop (tells Woz to repeat command)</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-learn-puzzles-section" aria-hidden="true">
                    <div className="small-12 cell">
                        <h1 className="section-title">Learn with Puzzles</h1>
                        <p className="section-subtitle show-for-medium">Click a puzzle below to learn more</p>
                    </div>
                    <div className="small-12 cell show-for-medium puzzle-list-container">
                        <ul className="puzzles-list">
                            <li className={ `puzzle-list-item ${ this.state.selected_puzzle === 0 ? 'selected' : '' }` } onClick={ () => this.changePuzzleDetails(0) }>
                                <p className="puzzle-list-item--title">Donut Detective</p>
                                <div className="puzzle-list-item--image">
                                    <img alt="" src={`${imgURL}/flagship/how-it-works/lp_donut_detective_small.jpg`} aria-hidden="true"/>
                                </div>
                            </li>
                            <li className={ `puzzle-list-item ${ this.state.selected_puzzle === 1 ? 'selected' : '' }` } onClick={ () => this.changePuzzleDetails(1) }>
                                <p className="puzzle-list-item--title">Tool Trouble</p>
                                <div className="puzzle-list-item--image">
                                    <img alt="" src={`${imgURL}/flagship/how-it-works/lp_tool_trouble_small.jpg`} aria-hidden="true"/>
                                </div>
                            </li>
                            <li className={ `puzzle-list-item ${ this.state.selected_puzzle === 2 ? 'selected' : '' }` } onClick={ () => this.changePuzzleDetails(2) }>
                                <p className="puzzle-list-item--title">Kite Plight</p>
                                <div className="puzzle-list-item--image">
                                    <img alt="" src={`${imgURL}/flagship/how-it-works/lp_kite_plight_small.jpg`} aria-hidden="true"/>
                                </div>
                            </li>
                            <li className={ `puzzle-list-item ${ this.state.selected_puzzle === 3 ? 'selected' : '' }` } onClick={ () => this.changePuzzleDetails(3) }>
                                <p className="puzzle-list-item--title">Puppy Problems</p>
                                <div className="puzzle-list-item--image">
                                    <img alt="" src={`${imgURL}/flagship/how-it-works/lp_puppy_problems_small.jpg`} aria-hidden="true"/>
                                </div>
                            </li>
                            <li className={ `puzzle-list-item ${ this.state.selected_puzzle === 4 ? 'selected' : '' }` } onClick={ () => this.changePuzzleDetails(4) }>
                                <p className="puzzle-list-item--title">Lunch Crunch</p>
                                <div className="puzzle-list-item--image">
                                    <img alt="" src={`${imgURL}/flagship/how-it-works/lp_lunch_crunch_small.jpg`} aria-hidden="true"/>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div id="puzzle-details" className={ `small-12 cell show-for-medium puzzle-details ${ this.state.puzzle_animating ? 'animating' : '' }`}>
                        <div className={ `puzzle-details--image puzzle-details--image__${ this.state.selected_puzzle }` }>
                            <img
                                alt=""
                                src={ this.state.selected_puzzle_image }
                                onLoad={ () => this.setState({ puzzle_image_loaded: true }) }
                                aria-hidden="true"
                            />
                        </div>
                        <div className="puzzle-details--content">
                            <p className="puzzle-level">{ this.state.selected_puzzle_level }</p>
                            <p className="puzzle-type">{ this.state.selected_puzzle_type }</p>
                            <p className="puzzle--type-desc">{ this.state.selected_puzzle_type_desc }</p>
                            <div className="puzzle-help-with has-simple-ideology-list">
                                <p className="puzzle-help-with--title">Helps With</p>
                                <ul className="has-simple-ideology-list--ideologies">
                                {
                                    __map(this.state.selected_puzzle_ideologies, (ideology, i) => {
                                        return (
                                            <li key={ i }>
                                                <img alt="" className="ideology-block--icon" src={ `${imgURL}/flagship/how-it-works/icon_${ __snakeCase(ideology) }.png` } aria-hidden="true" />
                                                {
                                                    ideology.indexOf(' ') > -1 ? (
                                                        <p className="ideology-block--text" dangerouslySetInnerHTML={{ __html: ideology.replace(' ', '<br aria-hidden="true"/>') }}></p>
                                                    ) : (
                                                        <p className="ideology-block--text">{ ideology }</p>
                                                    )
                                                }
                                            </li>
                                        )
                                    })
                                }
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="small-12 cell hide-for-medium puzzle-list-container">
                        <ul className="mobile-puzzles-list">
                            <li>
                                <p className="puzzle-list-item--title">Donut Detective</p>
                                <p>
                                    Player Level: Beginner<br />
                                    Puzzle Type: Sequencing<br />
                                    The step-by-step order in which instructions should occur
                                </p>
                            </li>
                            <li>
                                <p className="puzzle-list-item--title">Tool Trouble</p>
                                <p>
                                    Player Level: Beginner<br />
                                    Puzzle Type: Loops<br />
                                    A set of instructions that is repeated over and over again
                                </p>
                            </li>
                            <li>
                                <p className="puzzle-list-item--title">Kite Plight</p>
                                <p>
                                    Player Level: Intermediate<br />
                                    Puzzle Type: Advanced Sequencing<br />
                                    Reinforces the core computer science concept of sequencing
                                </p>
                            </li>
                            <li>
                                <p className="puzzle-list-item--title">Puppy Problems</p>
                                <p>
                                    Player Level: Advanced<br />
                                    Puzzle Type: Events<br />
                                    An action or cue that signals a new line of code to run
                                </p>
                            </li>
                            <li>
                                <p className="puzzle-list-item--title">Lunch Crunch</p>
                                <p>
                                    Player Level: Advanced<br />
                                    Puzzle Type: Conditional Statements<br />
                                    Actions that execute based on certain provisions
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-game-maker-section" aria-hidden="true">
                    <div className="small-12 cell">
                        <h1 className="section-title">Create with Game Maker</h1>
                        <p className="section-subtitle">
                            Once kids have mastered the concepts learned in the puzzles, <br className="show-for-medium" aria-hidden="true" />
                            they are ready to make their own games.
                        </p>
                    </div>
                    <div className="small-12 cell has-simple-ideology-list">
                        <ul className="has-simple-ideology-list--ideologies">
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_empathy.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Empathy</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_creativity.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Creativity</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_reading.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Reading</p>
                            </li>
                        </ul>
                        <div className="screenshot-info">
                            <img alt="" src={`${imgURL}/flagship/how-it-works/game_maker_screen.png`} aria-hidden="true" />
                        </div>
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-master-automation-section" aria-hidden="true">
                    <div className="small-12 cell">
                        <h1 className="section-title">Mastering Automation - Pet Pals</h1>
                        <p className="section-subtitle">
                            Pet Pals is a fun way to use creative problem solving through <br className="show-for-medium" aria-hidden="true" />
                            taking care of (and picking up after) lots of pets.
                        </p>
                    </div>
                    <div className="small-12 cell has-simple-ideology-list">
                        <ul className="has-simple-ideology-list--ideologies">
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_empathy.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Empathy</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_creativity.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Creativity</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_logic.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Logic</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_problem_solving.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Problem<br aria-hidden="true" />Solving</p>
                            </li>
                            <li className="ideology-block">
                                <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_pattern_recognition.png`} aria-hidden="true" />
                                <p className="ideology-block--text">Pattern<br aria-hidden="true" />Recognition</p>
                            </li>
                        </ul>
                        <div className="screenshot-info">
                            <img alt="" src={`${imgURL}/flagship/how-it-works/master_auto_screen.png`} aria-hidden="true" />
                        </div>
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-mini-games-section" aria-hidden="true">
                    <div className="small-12 cell">
                        <h1 className="section-title">Mini-games</h1>
                        <p className="section-subtitle">
                            Mini-games helps kids learn additional concepts <br className="show-for-medium" aria-hidden="true" />
                            that may apply to STEM subjects.
                        </p>
                    </div>
                    <div className="small-12 cell mini-games">
                        <div className="sweet-sorter game">
                            <div className="game-image">
                                <img alt="" src={`${imgURL}/flagship/how-it-works/sweet_sorter_shot.png`} aria-hidden="true" />
                            </div>
                            <div className="game-info has-simple-ideology-list">
                                <p className="game-title">Sweet Sorter</p>
                                <ul className="has-simple-ideology-list--ideologies">
                                    <li className="ideology-block">
                                        <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_math.png`} aria-hidden="true" />
                                        <p className="ideology-block--text">Math</p>
                                    </li>
                                    <li className="ideology-block">
                                        <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_pattern_recognition.png`} aria-hidden="true" />
                                        <p className="ideology-block--text">Pattern<br aria-hidden="true" />Recognition</p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="pet-stacker game">
                            <div className="game-image">
                                <img alt="" src={`${imgURL}/flagship/how-it-works/pet_stacker.png`} aria-hidden="true" />
                            </div>
                            <div className="game-info has-simple-ideology-list">
                                <p className="game-title">Pet Stacker</p>
                                <ul className="has-simple-ideology-list--ideologies">
                                    <li className="ideology-block">
                                        <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_math.png`} aria-hidden="true" />
                                        <p className="ideology-block--text">Math</p>
                                    </li>
                                    <li className="ideology-block">
                                        <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_pattern_recognition.png`} aria-hidden="true" />
                                        <p className="ideology-block--text">Pattern<br aria-hidden="true" />Recognition</p>
                                    </li>
                                    <li className="ideology-block">
                                        <img alt="" className="ideology-block--icon" src={`${imgURL}/flagship/how-it-works/icon_problem_solving.png`} aria-hidden="true" />
                                        <p className="ideology-block--text">Problem<br aria-hidden="true" />Solving</p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="small-12 cell show-for-medium screenshot-info">
                        <img alt="" src={`${imgURL}/flagship/how-it-works/mini_games_screen.png`} aria-hidden="true" />
                    </div>
                </div>
                <div className="grid-x grid-padding-x show-for-medium how-meet-foos-section" aria-hidden="true">
                    <div className="small-12 cell section-title-image">
                        <img alt="Meet The Foos" src={`${imgURL}/flagship/how-it-works/meet_the_foos.png`} />
                    </div>
                    <div className="small-12 cell foos-carousel">
                        <Slider { ...slickSettings }>
                            <div className="foo-slide">
                                <div className="a-foo glitch">
                                    <div className="front-info">
                                        <p className="a-foo--name">The Glitch</p>
                                        <p className="a-foo--job">Job: Causing trouble<br aria-hidden="true" />(but they mean well)</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            The Glitch appears whenever there&rsquo;s a system error in Fooville.
                                            Once falsely blamed for the errors, it&rsquo;s now known that The Glitch
                                            happens to be attracted to the electricity the system errors emit.
                                            The Glitch now helps out by &ldquo;sniffing out&rdquo; system errors caused by
                                            viruses.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo gracie">
                                    <div className="front-info">
                                        <p className="a-foo--name">Gracie</p>
                                        <p className="a-foo--job">Job: Fooville Police</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Gracie is a Police Foo who specializes in catching Glitches and viruses.
                                            She also finds missing pets like in her &ldquo;Runaway Pets&rdquo; case files. Loves donuts.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo woz">
                                    <div className="front-info">
                                        <p className="a-foo--name">Woz</p>
                                        <p className="a-foo--job">Job: Construction Expert</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Woz loves his tools and is incredibly attached to them.
                                            He&rsquo;s also heading the construction of Foover Dam. A perfectionist,
                                            Woz tends to redo projects over and over again, much to his friends&rsquo;
                                            chagrin.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo naomi">
                                    <div className="front-info">
                                        <p className="a-foo--name">Naomi</p>
                                        <p className="a-foo--job">Job: Covert Code Operative<br aria-hidden="true" />(aka Ninja)</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Naomi fervently studies a set of scrolls called &ldquo;The Code,&rdquo; passed down from
                                            Master Fooma. Her greatest rival is the white ninja Ichi, who once octopus-napped
                                            her best friend Hachi the OctoFoo.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo val">
                                    <div className="front-info">
                                        <p className="a-foo--name">Val</p>
                                        <p className="a-foo--job">Job: Data Cargo Transporter<br aria-hidden="true" />(aka AstroFoo Pilot)</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Val is an AstroFoo from FASA Space Station. She has traveled across vast systems,
                                            but currently enjoys the quiet life as a data cargo transporter. Val loves
                                            her puppies, who often come with her on her trips.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo sarge">
                                    <div className="front-info">
                                        <p className="a-foo--name">Sarge</p>
                                        <p className="a-foo--job">Job: Chef</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            A veteran from the infamous Great Food Fight Catastrophe of Foo York,
                                            Sarge now uses food to &ldquo;bring Foos together instead of separating
                                            them.&rdquo; Sarge loves to go fishing with his PetFoo pig Nugget.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo calamity">
                                    <div className="front-info">
                                        <p className="a-foo--name">Calamity</p>
                                        <p className="a-foo--job">Job: Animal Trainer and Lassoist</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Originally a lassoist, Calamity now trains animals for her acrobatic
                                            show. She once lassoed the elusive White Buffoolo but, feeling sorry
                                            for animal, let the Buffoolo go. This inspired her to train wayward
                                            pets for her wild west show. Sarge&rsquo;s pet pig Nugget, is actually one
                                            of Calamity&rsquo;s former wild west stars.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo queen">
                                    <div className="front-info">
                                        <p className="a-foo--name">Queen Candy</p>
                                        <p className="a-foo--job">Job: Royalty</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Queen &ldquo;Candy&rdquo; Candice is the current ruler of Sweetopia, a province west of Fooville.
                                            Sweetopia&rsquo;s power and agriculture is powered by the area&rsquo;s abundant sugary treats
                                            that are regularly collected and sorted.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="foo-slide">
                                <div className="a-foo sam">
                                    <div className="front-info">
                                        <p className="a-foo--name">Safari Sam</p>
                                        <p className="a-foo--job">Job: Zoologist<br aria-hidden="true" />(aka Pet Pals Center Operator)</p>
                                        <div className="a-foo--image"></div>
                                        <div className="a-foo--turn-icon"><i className="fa fa-refresh" aria-hidden="true"></i></div>
                                    </div>
                                    <div className="back-info">
                                        <p>
                                            Sam currently runs the Pet Pals Pet Center, which provides care, grooming, and shelter
                                            for Fooville&rsquo;s PetFoos. Sam has a Ph.d in Zoology, and has published many research papers
                                            on the behavioral and psychosomatic effects of hats on PetFoos.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Slider>
                    </div>
                </div>
                <div className="grid-x grid-padding-x how-awards-section" aria-hidden="true">
                    <div className="small-12 cell">
                        <h1 className="section-title">Awards</h1>
                    </div>
                    <div className="small-12 cell">
                        <ul className="awards-list">
                            <li className="award">
                                <img alt="American Association of School Librarians Best App 2016" src={`${imgURL}/flagship/teacher-landing/awards/AASL_Digital+Tools+Circle_2020.png`} />
                            </li>
                            <li className="award">
                                <img alt="Academics Choice Brain Toy Award" src={`${imgURL}/flagship/how-it-works/awards/award_brain_toy_trans.jpg`} />
                            </li>
                            <li className="award">
                                <img alt="Tillywig Toy Award - Brain Child" src={`${imgURL}/flagship/how-it-works/awards/brain_child_gold.png`} />
                            </li>
                            <li className="award">
                                <img alt="KAPi Award" src={`${imgURL}/flagship/how-it-works/awards/kapi-winner.png`} />
                            </li>
                            <li className="award">
                                <img alt="Kidscreen Award 2016" src={`${imgURL}/flagship/how-it-works/awards/kidscreen_award.png`} />
                            </li>
                            <li className="award">
                                <a href="https://www.kidsafeseal.com/certifiedproducts/codespark_thefoos_app.html" target="_blank" rel="noopener noreferrer">
                                    <img alt="kidSAFE Seal Program" className="kidsafe" src="https://www.kidsafeseal.com/sealimage/1730524102862329251/codespark_thefoos_app_large_darktm.png" />
                                </a>
                            </li>
                            <li className="award">
                                <img alt="Parents' Choice Gold Award" src={`${imgURL}/flagship/how-it-works/awards/parents_choice_gold.png`} />
                            </li>
                        </ul>
                    </div>
                    <div className="small-12 cell border-shadow"></div>
                </div>
                <div className="grid-x grid-padding-x how-action-section" aria-hidden="true">
                    <div className="small-12">
                        <h1 className="title">Try our top-rated <br className="show-for-medium" aria-hidden="true" />app FREE for 7 days!</h1>
                        <Button extraClass="show-for-medium" text="Start your free trial" type="in-page" onClick={ (e) => this.props.goToLink(e, process.env.REACT_APP_ACCOUNTS_BASE_URL) } />
                        <Button extraClass="hide-for-medium" text="Start your trial" type="in-page" onClick={ (e) => this.props.goToLink(e, process.env.REACT_APP_ACCOUNTS_BASE_URL) } />
                    </div>
                </div>
            </div>
        );
    }
}

HowItWorks.contextTypes = {
    mixpanel: PropTypes.object
};

export default props => (
    <Curious>
        {({ router, response, navigation }) => {
            return <HowItWorks router={ router } response={ response } { ...props } />;
        }}
    </Curious>
);



// WEBPACK FOOTER //
// ./src/containers/HowItWorks/index.js